// frontend/src/pages/superAdmin/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Dashboard = ({ user, setUser }) => {
  const [fiscalYear, setFiscalYear] = useState(null);
  const [schools, setSchools] = useState([]);
  const [userStats, setUserStats] = useState({ total: 0, active: 0, superAdmin: 0, admin: 0, referee: 0 });
  const [respondentStats, setRespondentStats] = useState({ iit: 0, eit1: 0, eit2: 0 });
  const [ratingStats, setRatingStats] = useState({
    excellent: 0,
    good: 0,
    fair: 0,
    needImprovement: 0,
    needUrgentImprovement: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch active fiscal year
        const fiscalYearRes = await api.get('/fiscal-years/active');
        const activeFiscalYear = fiscalYearRes.data;
        setFiscalYear(activeFiscalYear);

        if (activeFiscalYear) {
          // Fetch schools
          const schoolsRes = await api.get('/schools');
          setSchools(schoolsRes.data);

          // Fetch user statistics
          const usersRes = await api.get('/users');
          const users = usersRes.data;
          
          setUserStats({
            total: users.length,
            active: users.filter(u => u.active).length,
            superAdmin: users.filter(u => u.role === 'super_admin').length,
            admin: users.filter(u => u.role === 'admin').length,
            referee: users.filter(u => u.role === 'referee').length
          });

          // Fetch respondent statistics
          const quotasRes = await api.get(`/quotas/${activeFiscalYear.id}`);
          const quotas = quotasRes.data;
          
          let iitTotal = 0;
          let eit1Total = 0;
          let eit2Total = 0;
          
          quotas.forEach(quota => {
            iitTotal += quota.iit_count;
            eit1Total += quota.eit1_count;
            eit2Total += quota.eit2_count;
          });
          
          setRespondentStats({
            iit: iitTotal,
            eit1: eit1Total,
            eit2: eit2Total
          });

          // Fetch school ratings
          const ratingsRes = await api.get(`/reports/all/${activeFiscalYear.id}`);
          const ratings = ratingsRes.data;
          
          setRatingStats({
            excellent: ratings.filter(r => r.total_score >= 95).length,
            good: ratings.filter(r => r.total_score >= 85 && r.total_score < 95).length,
            fair: ratings.filter(r => r.total_score >= 75 && r.total_score < 85).length,
            needImprovement: ratings.filter(r => r.total_score >= 65 && r.total_score < 75).length,
            needUrgentImprovement: ratings.filter(r => r.total_score < 65).length
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Generate chart data
  const userChartData = {
    labels: ['ผู้ดูแลระบบ', 'ผู้ดูแลโรงเรียน', 'กรรมการประเมิน'],
    datasets: [
      {
        data: [userStats.superAdmin, userStats.admin, userStats.referee],
        backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc'],
        hoverBackgroundColor: ['#2e59d9', '#17a673', '#2c9faf'],
        borderWidth: 1,
      },
    ],
  };

  const respondentChartData = {
    labels: ['IIT', 'EIT1', 'EIT2'],
    datasets: [
      {
        data: [respondentStats.iit, respondentStats.eit1, respondentStats.eit2],
        backgroundColor: ['#f6c23e', '#e74a3b', '#6f42c1'],
        hoverBackgroundColor: ['#dda20a', '#be2617', '#5a359c'],
        borderWidth: 1,
      },
    ],
  };

  const ratingChartData = {
    labels: ['ผ่านดีเยี่ยม', 'ผ่านดี', 'ผ่าน', 'ต้องปรับปรุง', 'ต้องปรับปรุงโดยด่วน'],
    datasets: [
      {
        label: 'จำนวนโรงเรียน',
        data: [
          ratingStats.excellent,
          ratingStats.good,
          ratingStats.fair,
          ratingStats.needImprovement,
          ratingStats.needUrgentImprovement
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(255, 99, 132, 0.6)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="wrapper">
      <Header user={user} title="แดชบอร์ด" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">แดชบอร์ด</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item"><Link to="/super-admin/dashboard">หน้าหลัก</Link></li>
                  <li className="breadcrumb-item active">แดชบอร์ด</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            {loading ? (
              <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">กำลังโหลด...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="row">
                  <div className="col-lg-3 col-6">
                    <div className="small-box bg-info">
                      <div className="inner">
                        <h3>{schools.length}</h3>
                        <p>โรงเรียนทั้งหมด</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-school"></i>
                      </div>
                      <Link to="/super-admin/schools" className="small-box-footer">
                        ข้อมูลเพิ่มเติม <i className="fas fa-arrow-circle-right"></i>
                      </Link>
                    </div>
                  </div>

                  <div className="col-lg-3 col-6">
                    <div className="small-box bg-success">
                      <div className="inner">
                        <h3>{userStats.total}</h3>
                        <p>ผู้ใช้งานทั้งหมด</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-users"></i>
                      </div>
                      <Link to="/super-admin/users" className="small-box-footer">
                        ข้อมูลเพิ่มเติม <i className="fas fa-arrow-circle-right"></i>
                      </Link>
                    </div>
                  </div>

                  <div className="col-lg-3 col-6">
                    <div className="small-box bg-warning">
                      <div className="inner">
                        <h3>{respondentStats.iit + respondentStats.eit1 + respondentStats.eit2}</h3>
                        <p>ผู้ตอบแบบสอบถามทั้งหมด</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-clipboard-list"></i>
                      </div>
                      <Link to="/super-admin/reports" className="small-box-footer">
                        ข้อมูลเพิ่มเติม <i className="fas fa-arrow-circle-right"></i>
                      </Link>
                    </div>
                  </div>

                  <div className="col-lg-3 col-6">
                    <div className="small-box bg-danger">
                      <div className="inner">
                        <h3>{fiscalYear ? fiscalYear.year : '-'}</h3>
                        <p>ปีงบประมาณปัจจุบัน</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-calendar-alt"></i>
                      </div>
                      <Link to="/super-admin/fiscal-years" className="small-box-footer">
                        ข้อมูลเพิ่มเติม <i className="fas fa-arrow-circle-right"></i>
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">ผู้ใช้งานระบบ</h3>
                      </div>
                      <div className="card-body">
                        <div className="chart-container" style={{ height: '260px' }}>
                          <Pie data={userChartData} options={{ maintainAspectRatio: false }} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">ผู้ตอบแบบสอบถาม</h3>
                      </div>
                      <div className="card-body">
                        <div className="chart-container" style={{ height: '260px' }}>
                          <Pie data={respondentChartData} options={{ maintainAspectRatio: false }} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">สถานะการใช้งาน</h3>
                      </div>
                      <div className="card-body p-2">
                        <table className="table table-sm">
                          <tbody>
                            <tr>
                              <td>ผู้ใช้งานที่เปิดใช้งาน</td>
                              <td>
                                <span className="badge bg-success">{userStats.active}</span>
                              </td>
                            </tr>
                            <tr>
                              <td>ผู้ใช้งานที่ปิดใช้งาน</td>
                              <td>
                                <span className="badge bg-danger">{userStats.total - userStats.active}</span>
                              </td>
                            </tr>
                            <tr>
                              <td>โรงเรียนทั้งหมด</td>
                              <td>
                                <span className="badge bg-primary">{schools.length}</span>
                              </td>
                            </tr>
                            <tr>
                              <td>ปีงบประมาณ</td>
                              <td>
                                <span className="badge bg-info">{fiscalYear ? fiscalYear.year : '-'}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">สรุปผลการประเมิน</h3>
                      </div>
                      <div className="card-body">
                        <div className="chart-container" style={{ height: '300px' }}>
                          <Bar 
                            data={ratingChartData} 
                            options={{
                              maintainAspectRatio: false,
                              scales: {
                                y: {
                                  beginAtZero: true,
                                  precision: 0
                                }
                              },
                              plugins: {
                                title: {
                                  display: true,
                                  text: `ปีงบประมาณ ${fiscalYear ? fiscalYear.year : '-'}`
                                }
                              }
                            }} 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">การตั้งค่าระบบ</h3>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-3">
                            <div className="info-box bg-light">
                              <div className="info-box-content">
                                <span className="info-box-text">จัดการผู้ใช้งาน</span>
                                <Link to="/super-admin/users" className="btn btn-sm btn-primary mt-2">
                                  <i className="fas fa-users"></i> ดำเนินการ
                                </Link>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="info-box bg-light">
                              <div className="info-box-content">
                                <span className="info-box-text">จัดการแบบสอบถาม</span>
                                <Link to="/super-admin/questionnaires" className="btn btn-sm btn-primary mt-2">
                                  <i className="fas fa-clipboard-list"></i> ดำเนินการ
                                </Link>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="info-box bg-light">
                              <div className="info-box-content">
                                <span className="info-box-text">จัดการกรรมการ</span>
                                <Link to="/super-admin/referee-assignments" className="btn btn-sm btn-primary mt-2">
                                  <i className="fas fa-user-check"></i> ดำเนินการ
                                </Link>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="info-box bg-light">
                              <div className="info-box-content">
                                <span className="info-box-text">ดูรายงานผล</span>
                                <Link to="/super-admin/reports" className="btn btn-sm btn-primary mt-2">
                                  <i className="fas fa-chart-bar"></i> ดำเนินการ
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;